import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { trackShipment } from '../../../actions/trackShipmentActions';
import { validateShipmentTrackingNumberExternal } from '../../../helpers/formHelpers/formHelpers';
import { getQueryParam } from '../../../helpers/url/urlHelpers';
import ITrackShipmentReducer from '../../../interfaces/trackShipment/trackShipmentReducer';
import IMediaItem from '../../../interfaces/umbracoNodes/mediaItem';
import IUrlPickerItem from '../../../interfaces/umbracoNodes/urlPickerItem';
import { IStore } from '../../../reducers';
import SVGIcon from '../../base/SVGIcon';
import RichTextTitle from '../../base/richTextTitle';
import Button from '../../button-v2';
import ResultsLoader from '../../trackShipment/trackShipmentResults/resultsLoader';
import TrackShipmentIcons from '../../trackShipment/trackShipmentResults/trackShipmentIcons';
import {
	ErrorDescription,
	ErrorMessage,
	ResultContentContainer,
	ResultError,
	ResultWrapper,
} from './trackShipmentResult.styles';
import TrackShipmentInfo from './trackShipmentResultInfo';
import TrackShipmentTimeline from './trackShipmentResultTimeline';

interface TProps {
	invalidNumberErrorIcon: IMediaItem;
	invalidNumberErrorTitle: string;
	invalidNumberErrorDescription: string;
	unknownNumberErrorIcon: IMediaItem;
	unknownNumberErrorTitle: string;
	unknownNumberErrorDescription: string;
	unknownNumberRedirectLink: IUrlPickerItem;
}

const TrackShipmentResult: FC<TProps> = ({
	invalidNumberErrorIcon,
	invalidNumberErrorTitle,
	invalidNumberErrorDescription,
	unknownNumberErrorIcon,
	unknownNumberErrorTitle,
	unknownNumberErrorDescription,
	unknownNumberRedirectLink,
}) => {
	const dispatch = useDispatch<any>();
	const trackingNumberQuery =
		getQueryParam('q') || getQueryParam('TrackingNumber') || getQueryParam('trackingNumber') || '';
	const [trackingNumber, setTrackingNumber] = useState(trackingNumberQuery);
	const [isValidShipmentNumber, setValidShipmentNumber] = useState(true);

	useEffect(() => {
		if (trackingNumberQuery) {
			const isValidShipmentTrackingNumber = validateShipmentTrackingNumberExternal(trackingNumberQuery);
			if (isValidShipmentTrackingNumber) {
				dispatch(trackShipment(trackingNumberQuery));
			}
			setValidShipmentNumber(isValidShipmentTrackingNumber);
			setTrackingNumber(trackingNumberQuery);
		}
	}, [dispatch, trackingNumberQuery]);

	const { t } = useTranslation('trackShipment');
	const trackShipmentReducer: ITrackShipmentReducer = useSelector<IStore, ITrackShipmentReducer>(
		store => store.trackShipmentReducer
	);
	const { trackShipmentLoading, trackShipmentError, shipmentInfo } = trackShipmentReducer;
	const internationalShipment: boolean = !trackShipmentLoading && shipmentInfo && !shipmentInfo.isDomestic;

	return (
		<ResultWrapper>
			{isValidShipmentNumber && (
				<>
					{trackShipmentLoading && <ResultsLoader />}
					{trackShipmentError && <RichTextTitle content={t('unknown-error')} type="h3" />}
					{!trackShipmentLoading &&
						!trackShipmentError &&
						shipmentInfo &&
						!shipmentInfo.error &&
						shipmentInfo.trackingNumber && (
							<ResultContentContainer>
								<TrackShipmentIcons internationalShipment={internationalShipment} />
								<TrackShipmentInfo shipmentInfo={shipmentInfo} />
								{shipmentInfo.events.length > 0 && (
									<TrackShipmentTimeline events={shipmentInfo.events} />
								)}
							</ResultContentContainer>
						)}
					{shipmentInfo && shipmentInfo.error && (
						<ResultError>
							<SVGIcon icon={unknownNumberErrorIcon} preserveDimensions />
							<ErrorMessage content={unknownNumberErrorTitle} type="h5" />
							<ErrorDescription>{unknownNumberErrorDescription}</ErrorDescription>
							<Button
								title={unknownNumberRedirectLink.Name}
								href={`${unknownNumberRedirectLink.Url}${trackingNumber}`}
								target={unknownNumberRedirectLink.Target}
								type="secondary"
							/>
						</ResultError>
					)}
				</>
			)}
			{!isValidShipmentNumber && (
				<ResultError>
					<SVGIcon icon={invalidNumberErrorIcon} preserveDimensions />
					<ErrorMessage content={invalidNumberErrorTitle} type="h5" />
					<ErrorDescription>{invalidNumberErrorDescription}</ErrorDescription>
				</ResultError>
			)}
		</ResultWrapper>
	);
};

export default TrackShipmentResult;
